import {
	ChangeEvent,
	FunctionComponent,
	MutableRefObject,
	useEffect,
	useRef,
	useState,
} from "react";
import { UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import { InputField } from "@/components/InputField/InputField";
import {
	MultiAutoComplete,
	MultiAutoCompleteItem,
} from "@/components/MultiAutoComplete/MultiAutoComplete";

import {
	CompanyEnrichAutoComplete,
	CompanyEnrichAutoCompleteItem,
} from "./CompanyEnrichAutoComplete";
import { CreateCompanyEnrich } from "../schema/companyEnrich";
import { SENIORITY_OPTIONS } from "../constants/seniority.constants";
import { DEPARTMENT_OPTIONS } from "../constants/department.constants";

// Minimal shape we need from the score persona category
interface PersonaCategory {
	id?: number;
	category: string;
	jobsToInclude: string[];
}

interface PersonFiltersSectionProps {
	formValues: Partial<CreateCompanyEnrich>;
	setValue: UseFormSetValue<CreateCompanyEnrich>;
	fetchPositions: (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;
	fetchCountries: (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;
	fetchSchools: (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;
	countryNameMap: MutableRefObject<Map<string, string>>;
	personaCategories?: PersonaCategory[];
	/** "Same as company location": while checked, the person location mirrors it (PREC-3666). */
	sameAsCompanyLocation?: boolean;
	onSameAsCompanyLocationChange?: (checked: boolean) => void;
}

export const PersonFiltersSection: FunctionComponent<PersonFiltersSectionProps> = ({
	formValues,
	setValue,
	fetchPositions,
	fetchCountries,
	fetchSchools,
	countryNameMap,
	personaCategories = [],
	sameAsCompanyLocation = false,
	onSameAsCompanyLocationChange,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`companyEnrich.${key}`);

	const [selectedPersonaCategories, setSelectedPersonaCategories] = useState<
		MultiAutoCompleteItem[]
	>([]);

	const manualPositionQueryRef = useRef<string[]>([]);
	const personaPositionJobsRef = useRef<string[]>([]);

	useEffect(() => {
		const personaJobs = new Set(personaPositionJobsRef.current);

		manualPositionQueryRef.current = (formValues.positionQuery ?? []).filter(
			(p) => !personaJobs.has(p)
		);
	}, [formValues.positionQuery]);

	const handlePersonaCategorySelect = (items?: MultiAutoCompleteItem[]) => {
		setSelectedPersonaCategories(items || []);

		const personaJobs = [
			...new Set(
				(items || []).flatMap(
					(item) => personaCategories.find((c) => c.id === item.id)?.jobsToInclude ?? []
				)
			),
		];

		personaPositionJobsRef.current = personaJobs;

		const merged = [...new Set([...manualPositionQueryRef.current, ...personaJobs])];

		setValue("positionQuery", merged, { shouldDirty: true, shouldValidate: true });
	};

	return (
		<>
			{/* Query field */}
			<div className="flex flex-row gap-4 w-full mb-4">
				<InputField
					handleChange={(event: ChangeEvent<HTMLInputElement>) => {
						setValue("query", event.target.value || null, {
							shouldDirty: true,
							shouldValidate: true,
						});
					}}
					label={ts("queryLabel")}
					name="query"
					placeholder={ts("queryPlaceholder")}
					value={formValues.query ?? ""}
				/>
			</div>

			{personaCategories.length > 0 && (
				<div className="flex flex-col gap-2 w-full mb-4">
					<MultiAutoComplete
						stayOn
						data={personaCategories
							.filter((c) => c.id != null)
							.map((c) => ({ id: c.id as number, title: c.category }))}
						selectedItems={selectedPersonaCategories}
						handleSelect={handlePersonaCategorySelect}
						placeholder={ts("personaCategoryPlaceholder")}
						label={ts("personaCategoryLabel")}
					/>
				</div>
			)}

			{/* Positions — manual edits just update positionQuery, no persona reset */}
			<div className="flex flex-col gap-4 w-full mb-4">
				<CompanyEnrichAutoComplete
					stayOn={true}
					allowCustomValues={true}
					fetchFn={fetchPositions}
					handleSelect={(value) => {
						const positions = value?.map((r) => r.id) || [];

						manualPositionQueryRef.current = positions;

						// If user removed something that was persona-derived, remove it from persona jobs too
						personaPositionJobsRef.current = personaPositionJobsRef.current.filter((job) =>
							positions.includes(job)
						);

						const merged = [...new Set([...positions, ...personaPositionJobsRef.current])];

						setValue("positionQuery", merged, { shouldDirty: true, shouldValidate: true });
					}}
					label={ts("positionQueryLabel")}
					placeholder={ts("positionQueryPlaceholder")}
					selectedItems={formValues.positionQuery?.map((r) => ({ id: r, title: r })) || []}
					testId="autocomplete-positionQuery"
				/>
			</div>

			{/* Seniority */}
			<div className="flex flex-row gap-4 w-full mb-4">
				<MultiAutoComplete
					stayOn
					data={SENIORITY_OPTIONS}
					handleSelect={(value) => {
						setValue("seniority", value?.map((r) => r.id.toString()) || [], {
							shouldDirty: true,
							shouldValidate: true,
						});
					}}
					label={ts("seniorityLabel")}
					placeholder={ts("seniorityPlaceholder")}
					selectedItems={
						formValues.seniority?.map((r) => ({
							id: r,
							title: SENIORITY_OPTIONS.find((s) => s.id === r)?.title || r,
						})) || []
					}
				/>
			</div>

			{/* Department */}
			<div className="flex flex-row gap-4 w-full mb-4">
				<MultiAutoComplete
					stayOn
					data={DEPARTMENT_OPTIONS}
					handleSelect={(value) => {
						setValue("department", value?.map((r) => r.id.toString()) || [], {
							shouldDirty: true,
							shouldValidate: true,
						});
					}}
					label={ts("departmentLabel")}
					placeholder={ts("departmentPlaceholder")}
					selectedItems={
						formValues.department?.map((r) => ({
							id: r,
							title: DEPARTMENT_OPTIONS.find((d) => d.id === r)?.title || r,
						})) || []
					}
				/>
			</div>

			{/* Countries */}
			<div className="flex flex-col gap-2 w-full mb-4">
				<CompanyEnrichAutoComplete
					stayOn
					disabled={sameAsCompanyLocation}
					fetchFn={fetchCountries}
					handleSelect={(value) => {
						setValue("countries", value?.map((r) => r.id) || [], {
							shouldDirty: true,
							shouldValidate: true,
						});
					}}
					label={ts("countriesLabel")}
					placeholder={ts("countriesPlaceholder")}
					selectedItems={
						formValues.countries?.map((r) => ({
							id: r,
							title: countryNameMap.current.get(r) || r,
						})) || []
					}
					testId="autocomplete-countries"
				/>
				{onSameAsCompanyLocationChange && (
					<Checkbox
						isChecked={sameAsCompanyLocation}
						testId="checkbox-same-as-company-location"
						onChange={() => onSameAsCompanyLocationChange(!sameAsCompanyLocation)}
					>
						{ts("searchDefaults.sameAsCompanyLocation")}
					</Checkbox>
				)}
			</div>

			{/* Universities (education) */}
			<div className="flex flex-row gap-4 w-full mb-4">
				<CompanyEnrichAutoComplete
					stayOn
					allowCustomValues
					fetchFn={fetchSchools}
					handleSelect={(value) => {
						const institutions = value?.map((r) => r.id) || [];

						setValue(
							"education",
							institutions.length ? { institutionNameQuery: institutions } : null,
							{ shouldDirty: true, shouldValidate: true }
						);
					}}
					label={ts("universitiesLabel")}
					placeholder={ts("universitiesPlaceholder")}
					selectedItems={
						formValues.education?.institutionNameQuery?.map((r) => ({ id: r, title: r })) || []
					}
					testId="autocomplete-universities"
				/>
			</div>

			{/* Date pickers: Company Tenure */}
			<div className="flex flex-col gap-2 w-full mb-4">
				<div className="text-sm font-medium">{ts("atCurrentCompanyLabel")}</div>
				<div className="flex flex-row gap-4">
					<InputField
						handleChange={(event: ChangeEvent<HTMLInputElement>) => {
							setValue("atCurrentCompanyAfter", event.target.value || null, {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						name="atCurrentCompanyAfter"
						placeholder={ts("atCurrentCompanyAfterPlaceholder")}
						type="date"
						label={ts("after")}
						value={formValues.atCurrentCompanyAfter ?? ""}
					/>
					<InputField
						handleChange={(event: ChangeEvent<HTMLInputElement>) => {
							setValue("atCurrentCompanyBefore", event.target.value || null, {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						label={ts("before")}
						name="atCurrentCompanyBefore"
						placeholder={ts("atCurrentCompanyBeforePlaceholder")}
						type="date"
						value={formValues.atCurrentCompanyBefore ?? ""}
					/>
				</div>
			</div>

			{/* Date pickers: Position Tenure */}
			<div className="flex flex-col gap-2 w-full mb-4">
				<div className="text-sm font-medium">{ts("atCurrentPositionLabel")}</div>
				<div className="flex flex-row gap-4">
					<InputField
						handleChange={(event: ChangeEvent<HTMLInputElement>) => {
							setValue("atCurrentPositionAfter", event.target.value || null, {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						label={ts("after")}
						name="atCurrentPositionAfter"
						placeholder={ts("atCurrentPositionAfterPlaceholder")}
						type="date"
						value={formValues.atCurrentPositionAfter ?? ""}
					/>
					<InputField
						handleChange={(event: ChangeEvent<HTMLInputElement>) => {
							setValue("atCurrentPositionBefore", event.target.value || null, {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						label={ts("before")}
						name="atCurrentPositionBefore"
						placeholder={ts("atCurrentPositionBeforePlaceholder")}
						type="date"
						value={formValues.atCurrentPositionBefore ?? ""}
					/>
				</div>
			</div>
		</>
	);
};
